import { StateControlAbstraction } from "../../state-control.abstraction";
import { effect, inject, signal, untracked } from "@angular/core";
import { TransactionView } from "../../../../models/view/transaction/transaction.view";
import { toObservable } from "@angular/core/rxjs-interop";
import { filter } from "rxjs";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { areArraysDifferent } from "@bitwarden/web-vault/app/shared/utils/helper-categories";

export interface FilterControlType {
  countries: string[];
  accounts: string[];
  institutions: string[];
  symbols: string[];
  categories?: string[];
  classifications?: string[];
}

const initialFilter: FilterControlType = {
  countries: [],
  accounts: [],
  institutions: [],
  symbols: [],
  categories: [],
  classifications: [],
};

/** Pushes `value` onto `list` only if it isn't already present, mutating `list` in place. Single
 * "unique add" primitive shared by generateOptions() (building each option list from scratch)
 * and ensureTransactionVisible() (widening an existing selection) below, instead of each
 * reimplementing the same includes()-then-push check in its own style. */
function addUnique(list: string[], value: string): void {
  if (!list.includes(value)) {
    list.push(value);
  }
}

export class FilterControl extends StateControlAbstraction {
  private userStore: UserStoreService = inject(UserStoreService);
  protected initialOptions: FilterControlType = initialFilter;
  protected selectionToApply: FilterControlType = initialFilter;

  /** Public signal for component to listen too **/
  private _options = signal<FilterControlType>(null);
  options = this._options.asReadonly();

  private _selected = signal<FilterControlType>(null);
  selected = this._selected.asReadonly();

  constructor() {
    super();
    // options.categories otherwise only refreshes when isDirtyFilteringOptions fires (i.e. on
    // transaction-collection changes — see CalculationBaseStoreService.onEnrichedAlignedCollectionChange).
    // A category created (or renamed/deleted) with no transactions yet never trips that flag, so
    // it can exist in categoryViews() while options.categories still lags behind — the category
    // renders as a selectable option (views-dropdown.component.ts reads categoryViews() directly)
    // but can never be selected, making "All Categories" permanently unreachable (#4175). This
    // keeps options.categories reactive to the same live source independent of that flag.
    effect(
      () => {
        const categoryViews = this.userStore.categories.categoryViews();
        if (!categoryViews) {
          return;
        }
        this.syncCategoryOptions(categoryViews.map((c) => c.id));
      },
      { allowSignalWrites: true },
    );
  }

  /**
   * Keeps options.categories (and the "all categories selected" invariant, if it held) in sync
   * with the live category list. Mirrors the reselect-all-on-category-change handling in
   * CalculationBaseStoreService.initialiseFilteringOptions, which only fires on the
   * transaction-triggered path.
   */
  private syncCategoryOptions(categories: string[]): void {
    // untracked: this method both reads and writes _options/_selected. Without it, the writes
    // below would retrigger the constructor effect that calls this method, wastefully re-running
    // it a second time (short-circuiting on the re-comparison) for the same categoryViews() emission.
    const currentOptions = untracked(this._options);
    if (!currentOptions) {
      return; // Not initialised yet — initialiseOptions() will seed categories from this same source.
    }
    const previousCategories = currentOptions.categories ?? [];
    if (categories.length === 0 && previousCategories.length > 0) {
      // An empty emission can mean "not yet loaded" as much as "user deleted every category"
      // (e.g. CategoryStoreCollectionService.triggerChanges() firing after a failed
      // createDefaultCategory() save). Treat it the same as the null-signal case above and
      // wait for a real reconciling emission rather than zeroing out options/selected.
      return;
    }
    if (!areArraysDifferent(previousCategories, categories)) {
      return;
    }

    this._options.set({ ...currentOptions, categories });
    this.initialOptions = { ...this.initialOptions, categories };

    const currentSelection = untracked(this._selected);
    if (!currentSelection) {
      return;
    }
    const hadAllCategoriesSelected = !areArraysDifferent(
      currentSelection.categories ?? [],
      previousCategories,
    );
    if (hadAllCategoriesSelected) {
      this._selected.set({ ...currentSelection, categories });
    } else {
      // Partial selection: drop any id that no longer exists (e.g. a deleted category), but
      // don't silently add a newly created one to an intentionally narrowed selection.
      this._selected.set({
        ...currentSelection,
        categories: this.reconcileCategorySelection(currentSelection.categories, categories),
      });
    }
  }

  /**
   * Filters `selectedIds` down to the ones still present in `validIds`. If every previously
   * selected id has become invalid, falls back to the full `validIds` list instead of returning
   * an empty selection — an empty categories selection makes applyFiltersOnTransaction reject
   * every transaction, and nothing downstream would otherwise notice and fix it (#4175 follow-up).
   * Single source of truth for both syncCategoryOptions and clearDirtySelection, which previously
   * disagreed on this fallback.
   *
   * An already-empty selectedIds has nothing that "became invalid" — it's an intentionally empty
   * selection, not a fully-invalidated one — so it must not take the fallback.
   */
  private reconcileCategorySelection(selectedIds: string[], validIds: string[]): string[] {
    if (!selectedIds?.length) {
      return [];
    }
    const stillValid = selectedIds.filter((id) => validIds.includes(id));
    return stillValid.length ? stillValid : validIds;
  }

  clear() {
    this._options.set(null);
    this._selected.set(null);
    this.initialOptions = null;
  }

  /** **<span style="color: red;">This is for store use only</span>** **/
  readonly selected$ = toObservable(this.selected).pipe(
    filter((x) => x !== null || typeof x !== "undefined"),
  );

  /** On the new transaction load initialise the options for all the transactions. This is the unfiltered list from the transactionStore **/
  initialiseOptions(transactions: TransactionView[]) {
    this._options.set(this.generateOptions(transactions));
    this.initialOptions = { ...this.options() }; // keep a copy for when we reset
  }

  /** Updating control selection **/
  updateSelection(selection: FilterControlType) {
    if (this.selected() === null) {
      this._selected.set({ ...this.options() });
    } else {
      this._selected.set({ ...selection });
    }
  }

  /**
   * Method that compares the options and the selections, removing any selection not included in options.
   * e.g., an account was deleted but was still selected.
   * It operates on the current selection state (`this.selected()`).
   */
  clearDirtySelection(currentSelection: FilterControlType) {
    const currentOptions = this.options();

    // Do nothing if options or selection haven't been initialized yet
    if (!currentOptions || !currentSelection) {
      return;
    }

    let selectionChanged = false;

    // Filter each category in the selection against the available options
    const validAccounts = currentSelection.accounts.filter((account) =>
      currentOptions.accounts.includes(account),
    );
    if (validAccounts.length !== currentSelection.accounts.length) {
      selectionChanged = true;
    }

    const validInstitutions = currentSelection.institutions.filter((inst) =>
      currentOptions.institutions.includes(inst),
    );
    if (validInstitutions.length !== currentSelection.institutions.length) {
      selectionChanged = true;
    }

    const validCountries = currentSelection.countries.filter((country) =>
      currentOptions.countries.includes(country),
    );
    if (validCountries.length !== currentSelection.countries.length) {
      selectionChanged = true;
    }

    const validSymbols = currentSelection.symbols.filter((symbol) =>
      currentOptions.symbols.includes(symbol),
    );
    if (validSymbols.length !== currentSelection.symbols.length) {
      selectionChanged = true;
    }

    const validCategories = this.reconcileCategorySelection(
      currentSelection.categories,
      currentOptions.categories,
    );
    // Length alone can't detect a change here: reconcileCategorySelection's fallback means a
    // fully-invalidated selection can come back the same length as (but different from) the
    // original — e.g. ["b"] (now-invalid) reconciled against ["a"] falls back to ["a"], same
    // length as ["b"] but a different selection.
    if (areArraysDifferent(validCategories, currentSelection.categories)) {
      selectionChanged = true;
    }

    const validClassifications = currentSelection.classifications?.filter((classification) =>
      currentOptions.classifications.includes(classification),
    );
    if (validClassifications.length !== currentSelection.classifications.length) {
      selectionChanged = true;
    }

    // If any items were removed, update the selection signal
    if (selectionChanged) {
      const newSelection: FilterControlType = {
        accounts: validAccounts,
        institutions: validInstitutions,
        countries: validCountries,
        symbols: validSymbols,
        categories: validCategories,
        classifications: validClassifications,
      };

      this._selected.set(newSelection);
    }
  }

  filterOnAccount(account: AccountView) {
    const selection = {
      countries: this.initialOptions?.countries,
      institutions: this.initialOptions?.institutions,
      symbols: this.initialOptions?.symbols,
      accounts: [account.id],
      categories: this.initialOptions?.categories,
      classifications: this.initialOptions?.classifications,
    };

    this.updateSelection(selection);
  }

  filterOnCategories(category: CategoryView) {
    const selection = {
      countries: this.initialOptions?.countries,
      institutions: this.initialOptions?.institutions,
      symbols: this.initialOptions?.symbols,
      accounts: this.initialOptions?.accounts,
      categories: [category.id],
      classifications: this.initialOptions?.classifications,
    };

    this.updateSelection(selection);
  }

  clearFilter() {
    this.updateSelection(initialFilter);
    this._options.set({ ...this.initialOptions });
    this._selected.set({ ...this.options() });
  }

  private generateOptions(transactions: TransactionView[]) {
    const accounts = transactions.reduce((acc: string[], t) => {
      addUnique(acc, t.accountLink.id);
      return acc;
    }, []);

    const institutions = transactions.reduce((acc: string[], t) => {
      addUnique(acc, t.c_institutionLink.id);
      return acc;
    }, []);

    const countries = transactions.reduce((acc: string[], t) => {
      addUnique(acc, t.c_country.id);
      return acc;
    }, []);

    const symbols = transactions.reduce((syms: string[], t) => {
      addUnique(syms, t.quantity.actualQuantity.symbol);
      return syms;
    }, []);

    const categories = (this.userStore.categories.categoryViews() ?? []).map(
      (category) => category.id,
    );

    const classifications = transactions.reduce((classifications: string[], t) => {
      for (const alloc of t.allocations) {
        addUnique(classifications, alloc.classificationLink.id);
      }
      return classifications;
    }, []);

    // Leave the already selected
    return {
      accounts,
      institutions,
      countries,
      symbols,
      categories,
      classifications,
    };
  }

  /**
   * Widens the current selection just enough to include `transaction`, without touching
   * anything else that's already selected/unselected. Used when a global search result is
   * hidden by the active page filters (#5058: clicking a search result must always be able to
   * open it, auto-adjusting filters as needed rather than failing silently).
   * No-op before initialisation (selected() is still null).
   */
  ensureTransactionVisible(transaction: TransactionView): void {
    const current = this.selected();
    if (!current) {
      return;
    }

    const next: FilterControlType = { ...current };
    let changed = false;

    // Adds the first of `candidates` present in `transaction` to `next[key]`, unless one of them
    // is already selected — applyFiltersOnTransaction's own checks are all "is at least one of
    // the transaction's values for this field selected" (a single-value field is just the
    // one-candidate case of that), so a single helper covers both.
    const includeAny = (key: keyof FilterControlType, candidates: readonly string[]) => {
      const values = (next[key] as string[]) ?? [];
      if (!candidates.length || candidates.some((candidate) => values.includes(candidate))) {
        return;
      }
      const widened = [...values];
      addUnique(widened, candidates[0]);
      next[key] = widened;
      changed = true;
    };

    // Mirror applyFiltersOnTransaction's own normalisation exactly: it compares these fields
    // against `?? ""` (accounts/institutions/countries) or `|| ""` (symbols), so a null/undefined
    // value there is itself treated as the real, comparable value "" — not as "missing". Widening
    // with anything else (e.g. skipping the field) would leave that dimension unwidened while
    // applyFiltersOnTransaction still requires "" to be selected, silently defeating the "always
    // able to open the result" guarantee.
    const asCandidates = (value: string | undefined | null): string[] => [value ?? ""];

    includeAny("accounts", asCandidates(transaction.accountLink.id));
    includeAny("institutions", asCandidates(transaction.c_institutionLink.id));
    includeAny("countries", asCandidates(transaction.c_country.id));
    includeAny("symbols", asCandidates(transaction.quantity.actualQuantity.symbol));
    includeAny("categories", transaction.c_categories ?? []);

    if (changed) {
      this.updateSelection(next);
    }
  }

  applyFiltersOnTransaction(collection: readonly TransactionView[]) {
    return collection.filter((transactionView) => {
      return (
        this.selected()?.accounts.includes(transactionView.accountLink.id ?? "") &&
        this.selected()?.institutions.includes(transactionView.c_institutionLink.id ?? "") &&
        this.selected()?.countries.includes(transactionView.c_country.id ?? "") &&
        this.selected()?.symbols.includes(transactionView.quantity.actualQuantity.symbol || "") &&
        this.selected()?.categories?.some((category) =>
          transactionView.c_categories.includes(category),
        )
        // TODO: add classifications filter when they get added to the UI
      );
    });
  }

  applyFiltersOnAccounts(collection: readonly AccountView[]) {
    const institutionViews = this.userStore.institutions.institutionViews() ?? [];
    return collection.filter((accountView) => {
      const institutionId = accountView.institutionLink.institutionId ?? "";
      const country =
        institutionViews.find((institution) => institution.id === institutionId)?.swift
          .countryCode ?? "";
      return (
        this.selected()?.accounts.includes(accountView.id ?? "") &&
        this.selected()?.institutions.includes(institutionId) &&
        this.selected()?.countries.includes(country)
      );
    });
  }

  allOptionsSelected(previousSelected: FilterControlType, previousOptions: FilterControlType) {
    return (
      previousSelected.accounts.length === previousOptions.accounts.length &&
      previousSelected.institutions.length === previousOptions.institutions.length &&
      previousSelected.countries.length === previousOptions.countries.length &&
      previousSelected.symbols.length === previousOptions.symbols.length &&
      previousSelected.categories.length === previousOptions.categories.length &&
      previousSelected.classifications.length === previousOptions.classifications.length
    );
  }
}
